const M_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/math';
const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

function mTag(tag: string, content: string, attrs = ''): string {
  return `<m:${tag}${attrs ? ' ' + attrs : ''}>${content}</m:${tag}>`;
}

function makeRun(text: string, sty: 'i' | 'n' | 'b' | 'bi' = 'n'): string {
  const rPr = sty !== 'n' ? mTag('rPr', mTag('sty', '', `m:val="${sty}"`)) : '';
  return mTag('r', rPr + mTag('t', escXml(text)));
}

function escXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function childElements(el: Element): Element[] {
  return Array.from(el.childNodes).filter((n): n is Element => n.nodeType === 1);
}

function convertChildren(el: Element): string {
  return childElements(el).map(convertElement).join('');
}

function convertElement(el: Element): string {
  switch (el.localName) {
    case 'math':
    case 'mrow':
    case 'mstyle':
    case 'mpadded':
    case 'mphantom':
    case 'merror':
      return convertChildren(el);

    case 'mi': {
      const t = el.textContent || '';
      const sty = t.length === 1 ? 'i' : 'n';
      return makeRun(t, sty);
    }
    case 'mn':
      return makeRun(el.textContent || '', 'n');
    case 'mo':
      return makeRun(el.textContent || '', 'n');
    case 'mtext':
      return makeRun(el.textContent || '', 'n');
    case 'mspace':
      return '';

    case 'mfrac': {
      const [num, den] = childElements(el);
      return mTag('f',
        mTag('fPr', '') +
        mTag('num', num ? convertElement(num) : '') +
        mTag('den', den ? convertElement(den) : '')
      );
    }

    case 'msqrt': {
      return mTag('rad',
        mTag('radPr', mTag('degHide', '', 'm:val="1"')) +
        mTag('deg', '') +
        mTag('e', convertChildren(el))
      );
    }

    case 'mroot': {
      const [base, index] = childElements(el);
      return mTag('rad',
        mTag('radPr', '') +
        mTag('deg', index ? convertElement(index) : '') +
        mTag('e', base ? convertElement(base) : '')
      );
    }

    case 'msup': {
      const [base, sup] = childElements(el);
      return mTag('sSup',
        mTag('sSupPr', mTag('ctrlPr', '')) +
        mTag('e', base ? convertElement(base) : '') +
        mTag('sup', sup ? convertElement(sup) : '')
      );
    }

    case 'msub': {
      const [base, sub] = childElements(el);
      return mTag('sSub',
        mTag('sSubPr', mTag('ctrlPr', '')) +
        mTag('e', base ? convertElement(base) : '') +
        mTag('sub', sub ? convertElement(sub) : '')
      );
    }

    case 'msubsup': {
      const [base, sub, sup] = childElements(el);
      return mTag('sSubSup',
        mTag('sSubSupPr', mTag('ctrlPr', '')) +
        mTag('e', base ? convertElement(base) : '') +
        mTag('sub', sub ? convertElement(sub) : '') +
        mTag('sup', sup ? convertElement(sup) : '')
      );
    }

    case 'munder': {
      const [base, lim] = childElements(el);
      const baseText = base?.textContent || '';
      if (/^[∑∏∫∬∭∮]$/.test(baseText)) {
        return mTag('nary',
          mTag('naryPr',
            mTag('chr', '', `m:val="${escXml(baseText)}"`) +
            mTag('limLoc', '', 'm:val="undOver"') +
            mTag('subHide', '', 'm:val="0"') +
            mTag('supHide', '', 'm:val="1"') +
            mTag('ctrlPr', '')
          ) +
          mTag('sub', lim ? convertElement(lim) : '') +
          mTag('sup', '') +
          mTag('e', '')
        );
      }
      return mTag('limLow',
        mTag('e', base ? convertElement(base) : '') +
        mTag('lim', lim ? convertElement(lim) : '')
      );
    }

    case 'mover': {
      const [base, over] = childElements(el);
      const overText = over?.textContent || '';
      const accents = new Set(['̂', '̃', '̄', '̇', '̈', '→', '⃗', '̱']);
      if (accents.has(overText) || overText.length === 1) {
        return mTag('acc',
          mTag('accPr',
            mTag('chr', '', `m:val="${escXml(overText)}"`) +
            mTag('ctrlPr', '')
          ) +
          mTag('e', base ? convertElement(base) : '')
        );
      }
      return mTag('limUpp',
        mTag('limUppPr', mTag('ctrlPr', '')) +
        mTag('e', base ? convertElement(base) : '') +
        mTag('lim', convertElement(over))
      );
    }

    case 'munderover': {
      const [base, sub, sup] = childElements(el);
      const baseText = base?.textContent || '';
      if (/^[∑∏∫∬∭∮]$/.test(baseText)) {
        return mTag('nary',
          mTag('naryPr',
            mTag('chr', '', `m:val="${escXml(baseText)}"`) +
            mTag('limLoc', '', 'm:val="undOver"') +
            mTag('ctrlPr', '')
          ) +
          mTag('sub', sub ? convertElement(sub) : '') +
          mTag('sup', sup ? convertElement(sup) : '') +
          mTag('e', '')
        );
      }
      return mTag('limUpp',
        mTag('limUppPr', mTag('ctrlPr', '')) +
        mTag('e', mTag('limLow',
          mTag('e', base ? convertElement(base) : '') +
          mTag('lim', sub ? convertElement(sub) : '')
        )) +
        mTag('lim', sup ? convertElement(sup) : '')
      );
    }

    case 'mfenced': {
      const open = el.getAttribute('open') ?? '(';
      const close = el.getAttribute('close') ?? ')';
      const sep = el.getAttribute('separators') ?? ',';
      const dPr = mTag('dPr',
        mTag('begChr', '', `m:val="${escXml(open)}"`) +
        mTag('endChr', '', `m:val="${escXml(close)}"`) +
        mTag('sepChr', '', `m:val="${escXml(sep[0] ?? ',')}"`) +
        mTag('ctrlPr', '')
      );
      const children = childElements(el);
      const eItems = children.map(c => mTag('e', convertElement(c))).join('');
      return mTag('d', dPr + eItems);
    }

    case 'mtable': {
      const rows = childElements(el).filter(c => c.localName === 'mtr');
      const mRows = rows.map(row => {
        const cells = childElements(row).filter(c => c.localName === 'mtd');
        const mCells = cells.map(cell => mTag('e', convertChildren(cell))).join('');
        return mTag('mr', mCells);
      }).join('');
      return mTag('m', mTag('mPr', mTag('ctrlPr', '')) + mRows);
    }

    case 'mmultiscripts': {
      const children = childElements(el);
      if (children.length >= 3) {
        const [base, sub, sup] = children;
        return mTag('sSubSup',
          mTag('sSubSupPr', mTag('ctrlPr', '')) +
          mTag('e', convertElement(base)) +
          mTag('sub', sub?.textContent === 'none' ? '' : (sub ? convertElement(sub) : '')) +
          mTag('sup', sup?.textContent === 'none' ? '' : (sup ? convertElement(sup) : ''))
        );
      }
      return convertChildren(el);
    }

    default:
      return convertChildren(el);
  }
}

// --- Minimal ZIP encoder (store/no-compression) ---

function crc32(data: Uint8Array): number {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    table[i] = c;
  }
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < data.length; i++) crc = table[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

function u16(n: number): Uint8Array { return new Uint8Array([n & 0xFF, (n >> 8) & 0xFF]); }
function u32(n: number): Uint8Array { return new Uint8Array([n & 0xFF, (n >> 8) & 0xFF, (n >> 16) & 0xFF, (n >> 24) & 0xFF]); }

function cat(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}

function makeZip(files: { name: string; content: string }[]): Uint8Array {
  const enc = new TextEncoder();
  const entries: { name: Uint8Array; data: Uint8Array; crc: number; offset: number }[] = [];
  const locals: Uint8Array[] = [];

  for (const file of files) {
    const name = enc.encode(file.name);
    const data = enc.encode(file.content);
    const crc = crc32(data);
    const offset = locals.reduce((s, l) => s + l.length, 0);
    locals.push(cat(
      new Uint8Array([0x50, 0x4B, 0x03, 0x04]),
      u16(20), u16(0), u16(0), u16(0), u16(0),
      u32(crc), u32(data.length), u32(data.length),
      u16(name.length), u16(0),
      name, data
    ));
    entries.push({ name, data, crc, offset });
  }

  const centralOffset = locals.reduce((s, l) => s + l.length, 0);
  const centrals = entries.map(e => cat(
    new Uint8Array([0x50, 0x4B, 0x01, 0x02]),
    u16(20), u16(20), u16(0), u16(0), u16(0), u16(0),
    u32(e.crc), u32(e.data.length), u32(e.data.length),
    u16(e.name.length), u16(0), u16(0), u16(0), u16(0),
    u32(0), u32(e.offset), e.name
  ));

  const centralSize = centrals.reduce((s, c) => s + c.length, 0);
  const eocd = cat(
    new Uint8Array([0x50, 0x4B, 0x05, 0x06]),
    u16(0), u16(0),
    u16(entries.length), u16(entries.length),
    u32(centralSize), u32(centralOffset),
    u16(0)
  );

  return cat(...locals, ...centrals, eocd);
}

// --- Public API ---

export function mathmlToDocx(mmlString: string): Uint8Array {
  const parser = new DOMParser();
  const doc = parser.parseFromString(mmlString, 'application/xml');
  const omml = convertElement(doc.documentElement);

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>`;

  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`;

  const wordRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>`;

  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:document xmlns:w="${W_NS}" xmlns:m="${M_NS}"><w:body><w:p><m:oMathPara><m:oMath>${omml}</m:oMath></m:oMathPara></w:p><w:sectPr/></w:body></w:document>`;

  return makeZip([
    { name: '[Content_Types].xml', content: contentTypes },
    { name: '_rels/.rels', content: rels },
    { name: 'word/document.xml', content: document },
    { name: 'word/_rels/document.xml.rels', content: wordRels },
  ]);
}
